import { db } from "../../../firebaseClient";
import { getStorage, ref, uploadString } from "firebase/storage";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  MESSAGE_STATUS_SUCCESS,
  MESSAGE_STATUS_FAILED,
  COLLECTION_NAME as MESSAGE_COLLECTION_NAME,
  MESSAGE_EVENT_PRODUCT_IMPORT,
} from "models/messages";
import { UploadProduct, DEFAULT_TRADEMARK } from "types/products";
import { CANONICAL_PRODUCT_HEADERS } from "types/onboarding";

export const ASIN_RE = /^[A-Z0-9]{10}$/i;
// eslint-disable-next-line no-control-regex
export const CONTROL_CHARS_RE = /[\x00-\x1F\x7F]/g;
export const ASIN_DP_RE = /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i;
export const ASIN_GP_RE = /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i;

export const sanitizeName = (s: string) =>
  s.replace(CONTROL_CHARS_RE, "").replace(/\s+/g, " ").trim();

export const extractASIN = (input: string): string | null => {
  if (!input) return null;
  const raw = input.trim();

  const fromDp = raw.match(ASIN_DP_RE);
  if (fromDp) return fromDp[1].toUpperCase();
  const fromGp = raw.match(ASIN_GP_RE);
  if (fromGp) return fromGp[1].toUpperCase();

  if (ASIN_RE.test(raw)) return raw.toUpperCase();
  return null;
};

export const uploadProductsToStorage = async (
  tenantId: string,
  products: UploadProduct[]
): Promise<string> => {
  const headers = [...CANONICAL_PRODUCT_HEADERS];
  const csvRows = [headers.join(",")];

  products.forEach((product) => {
    const row = [
      product.name,
      product.amazonId,
      product.sku || "",
      product.brand || "",
      product.upc || "",
      product.map || 500,
      DEFAULT_TRADEMARK,
    ].map((field) => `"${field}"`);
    csvRows.push(row.join(","));
  });

  const csvContent = csvRows.join("\n");

  const storage = getStorage();
  const timestamp = Date.now();
  const fileKey = `${tenantId}/productImport/${timestamp}.csv`;
  const storageRef = ref(storage, fileKey);

  await uploadString(storageRef, csvContent, "raw", {
    contentType: "text/csv",
  });
  return fileKey;
};

export const waitForProductImport = (tenantId: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const TWO_MINUTES = 120000;
    const FIVE_SECONDS = 5000;

    let q = query(
      collection(db, MESSAGE_COLLECTION_NAME),
      where("tenant", "==", tenantId),
      where("type", "==", MESSAGE_EVENT_PRODUCT_IMPORT),
      where("status", "in", [MESSAGE_STATUS_SUCCESS, MESSAGE_STATUS_FAILED])
    );

    const timeout = setTimeout(
      () => {
        unsub();
        reject(new Error("Product import timed out"));
      },
      process.env.REACT_APP_ENV === "local" ? FIVE_SECONDS : TWO_MINUTES
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const last = snapshot.docs[snapshot.docs.length - 1]?.data();
        if (!last) return;
        unsub();
        clearTimeout(timeout);
        last.status === MESSAGE_STATUS_SUCCESS
          ? resolve(true)
          : reject(new Error("Product import failed"));
      },
      (err) => {
        clearTimeout(timeout);
        unsub();
        reject(err);
      }
    );
  });
};
